import ProductName from "@/base-components/ProductName";
import { plannedVisit, ymdFromDate, ymdToString } from "@/generic/recurrence";
import React, { useMemo } from 'react';
import { Box, Button, Flex, Grid, GridItem, Input, Spacer } from '@chakra-ui/react';
import { RoutePath, SPACING } from '../../generic/config';
import { useNavigate } from 'react-router-dom';
import { localStorageKeys } from '@/generic/constants';
import { Context } from '../../App';
import Order, { ORDER_STATUS_OPTIONS, OrderModel, OrderRow, OrderRowSchema, OrderStatus } from '../../model/order';
import ModelInput from '../../base-components/ModelInput';
import Card from '../../base-components/Card';
import useModel, { getBasicModelStateless, Model } from '../../base-components/hooks/useModel';
import EditorParent from '@/base-components/EditorParent';
import Table, { TableProps } from '@/base-components/Table';
import Product from '@/model/product';
import { useQuery } from 'react-query';
import { QueryKeys } from '@/api/base/react-query';
import { getProducts } from '@/api/product-controller';
import useDialog from '@/base-components/hooks/useDialog';
import useToast from '@/base-components/hooks/useToast';
import { getWorksiteFullName } from '@/model/worksite';
import ConnectedWorksiteBox from './ConnectedWorksiteBox';
import Dropdown from '@/base-components/Dropdown';
import AttachedFilesGridItem from './AttachedFilesGridItem';
import { isDesktop } from 'react-device-detect';
import { previewAttachmentFromOrder } from '@/api/order-controller';
import ConnectedCustomerBox from './ConnectedCustomerBox';
import Text from '@/base-components/Text';
import Icon, { Icons } from '@/base-components/Icons';
import PropertyValuesCard from '@/base-components/PropertyValuesCard';
import { isEmptyDate, isSameDay } from '@/generic/date-utils';
import { capitalizeFirstLetter } from '@/generic/string-utils';
import { Translations } from '@/generic/Translations';

const OrderPage: React.FC = () => {
    const { translations, resources, language } = React.useContext(Context);
    const navigate = useNavigate();
    const urlParams = new URLSearchParams(window.location.search);
    const fromTasks = urlParams.get('fromTasks') === 'true';
    const OrderModelDefinition = useMemo(() => OrderModel(fromTasks), [fromTasks]);
    const { model, toolbar } = useModel<Order>(OrderModelDefinition);

    const updateDateOverride = (i: number, value: string) => {
        const extraData = { ...(model.object.extraData ?? {}) };
        const overrides: Record<string, string> = { ...(extraData.scheduledDateOverrides ?? {}) };
        if (value) overrides[String(i)] = value;
        else delete overrides[String(i)];
        model.setObject({
            ...model.object,
            extraData: { ...extraData, scheduledDateOverrides: Object.keys(overrides).length ? overrides : undefined },
        });
    };

    const onCopyOrder = () => {
        const orderContext: Partial<Order> = {
            worksite: model.object.worksite,
            orderRows: model.object.orderRows.map(row => ({
                ...row,
                actualQuantity: 0,
            })),
            express: model.object.express,
            priority: model.object.priority,
            recurringInterval: model.object.recurringInterval,
            dispatcherNotes: model.object.dispatcherNotes,
            employeeNotes: model.object.employeeNotes,
            resourceId: model.object.resourceId,
        };
        localStorage.setItem(localStorageKeys.editorPersistedContext, JSON.stringify(orderContext));
        navigate(`${RoutePath.Orders}/new`);
    };

    const title = model.object.id + (model.object.externalId ? (" (" + model.object.externalId + ")") : "")
    const dialog = useDialog();
    const toast = useToast();
    const resourcesDropdownItems = resources?.data?.map(resource => ({ id: resource.id, name: resource.code }));
    const currentResource = resourcesDropdownItems?.find(item => item.id === model.object.resourceId) || null;

    const addRow = (product: Product) => {
        if (model.object.orderRows.some(row => row.product.id === product.id)) {
            toast(translations.model.order.warnings.productMustBeUnique.title, translations.model.order.warnings.productMustBeUnique.description, "warning");

            return;
        }

        const newRowId = model.object.orderRows.reduce((max, row) => Math.max(max, row.rowId), 0) + 1;
        const newRow: OrderRow = {
            id: newRowId,
            product: product,
            orderedQuantity: model.isNew ? 0 : 1, // Default to 0 for new orders, 1 for existing orders
            actualQuantity: model.isNew ? 1 : 0, // Default to 1 for new orders, 0 for existing orders
            rowId: newRowId,
        };
        model.setObject({ ...model.object, orderRows: [...model.object.orderRows, newRow] });
        dialog.closeDialog();
        onEditRow(model, newRow, translations, dialog, () => {
            // On abort, remove the added row
            model.setObject(prevState => ({
                ...prevState,
                orderRows: prevState.orderRows.filter(r => r.rowId !== newRow.rowId)
            }));
        });
    }

    const onSearchProduct = () => {
        dialog.openDialog({
            title: translations.model.product.search.title,
            body: <ProductSearch onAddRow={addRow} />,
            buttons: []
        });
    }
    const dropdownItems = ORDER_STATUS_OPTIONS(translations);

    return (
        <EditorParent title={title} description={getWorksiteFullName(model.object.worksite)} toolbar={toolbar} actions={[{ title: translations.model.order.copyOrder, onClick: onCopyOrder }]}>
            <Grid templateColumns={isDesktop ? "repeat(10, 1fr)" : "repeat(1, 1fr)"} gap={SPACING} alignItems="stretch">
                <GridItem colSpan={isDesktop ? 5 : 1}>
                    <Card {...translations.model.order.cards.baseInfo} height={"100%"} rightAddon={<Box w="5ch"><ModelInput title={translations.model.order.express.title} fieldKey="express" model={model} /></Box>}>
                        <ModelInput title={translations.model.order.externalId} fieldKey="externalId" model={model} />
                        <ModelInput title={translations.model.order.scheduledDate} fieldKey="scheduledDate" model={model} />
                        {resourcesDropdownItems && <Dropdown title={translations.model.order.resource} items={resourcesDropdownItems} value={currentResource} onChange={(item) => model.setObject({ ...model.object, resourceId: resourcesDropdownItems.find(i => i.id === item?.id)?.id })} />}
                        <ModelInput title={translations.model.order.completionDate} fieldKey="completionDate" model={model} />
                        <ModelInput<Order> kind='dropdown' model={model} title={translations.model.order.status} fieldKey="status" dropdownProps={{
                            items: dropdownItems, onChange: (item) => {
                                // If completed, set completion date to today if not set
                                if (item?.id === OrderStatus.COMPLETED && (isSameDay(model.object.completionDate, new Date("1900-01-01")) || !model.object.completionDate)) {
                                    model.setObject(prevState => {
                                        if (prevState) {
                                            return { ...prevState, completionDate: new Date() };
                                        }
                                        return prevState;
                                    });
                                }
                            }
                        }} />
                    </Card>
                </GridItem>
                <GridItem colSpan={isDesktop ? 5 : 1}>
                    <Card {...translations.model.order.cards.recurringDetails}>
                        <ModelInput title={translations.model.order.recurringInterval} fieldKey="recurringInterval" model={model} />
                        <ModelInput title={translations.model.order.contractEndDate} fieldKey="contractEndDate" model={model} />
                        {model.object.recurringInterval > 0 && (
                            <>
                                <Text mt={SPACING} fontWeight="bold">{translations.model.order.futureOrders}</Text>
                                <Flex pl={SPACING / 2} flexDirection="column" justifyContent="space-between" gap={SPACING / 2}>
                                    {Array.from({ length: 5 }).map((_, i) => {
                                        const isThis = i === 0;
                                        const locale = language === 'SE' ? 'sv-SE' : 'en-US';
                                        // Same rule as the backend (generic/recurrence.ts): the series keeps its day of
                                        // the month and weekend moves don't carry over. Visit 0 is this order itself.
                                        const scheduled = ymdFromDate(new Date(model.object.scheduledDate));
                                        const planned = isThis ? scheduled
                                            : plannedVisit(scheduled, model.object.extraData as never, Number(model.object.recurringInterval), i);
                                        const calculatedDateStr = ymdToString(planned);
                                        const nextDate = new Date(planned.y, planned.m - 1, planned.d);
                                        const overrideDateStr = model.object.extraData?.scheduledDateOverrides?.[String(i)];
                                        const isBeforeEndDate = !model.object.contractEndDate || nextDate <= model.object.contractEndDate || isEmptyDate(model.object.contractEndDate);
                                        return isBeforeEndDate ? (
                                            <Flex key={i} mt={SPACING / 4} alignItems="center" gap={SPACING / 2}>
                                                {isThis ? (
                                                    <>
                                                        <Text variant='small' fontWeight="semibold">{capitalizeFirstLetter(nextDate.toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }))}</Text>
                                                        <Spacer />
                                                        <Text variant='extra-small' ml={SPACING}>({translations.model.order.thisOrder})</Text>
                                                        <Input
                                                            type="date"
                                                            size="xs"
                                                            w="8.75rem"
                                                            value={overrideDateStr ?? calculatedDateStr}
                                                            data-testid={`future-visit-${i}`}
                                                            disabled
                                                        />                                                    </>
                                                ) : (
                                                    <>
                                                        <Text variant='small'>{capitalizeFirstLetter((overrideDateStr ? new Date(overrideDateStr) : nextDate).toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }))}</Text>
                                                        <Spacer />
                                                        {overrideDateStr && <Text variant='extra-small' fontWeight="bold">({translations.model.order.manualDate})</Text>}
                                                        {overrideDateStr && <Icon icon={Icons.reload} size="2xs" cursor="pointer" onClick={() => updateDateOverride(i, '')} />}
                                                        <Input
                                                            type="date"
                                                            size="xs"
                                                            w="8.75rem"
                                                            value={overrideDateStr ?? calculatedDateStr}
                                                            data-testid={`future-visit-${i}`}
                                                            onChange={(e) => updateDateOverride(i, e.target.value === calculatedDateStr ? '' : e.target.value)}
                                                        />
                                                    </>
                                                )}
                                            </Flex>
                                        ) : null;
                                    })}
                                </Flex>
                            </>
                        )}
                    </Card>
                </GridItem>
                <GridItem colSpan={isDesktop ? 5 : 1}>
                    <Flex flexDirection="column" gap={SPACING} h="100%">
                        <Card {...translations.model.order.cards.customer}>
                            {model.object.worksite?.customer && (
                                <ConnectedCustomerBox customer={model.object.worksite.customer} showTitle={false} />
                            )}
                        </Card>
                        <Card {...translations.model.order.cards.worksite}>
                            <ConnectedWorksiteBox worksite={model.object.worksite} onConnect={model.isNew ? (worksite) => model.setObject({ ...model.object, worksite: { ...model.object.worksite, ...worksite, customer: worksite.customerHit } }) : undefined} showTitle={false} />
                        </Card>
                    </Flex>
                </GridItem>
                <GridItem colSpan={isDesktop ? 5 : 1}>
                    <Card {...translations.model.order.cards.notes}>
                        <ModelInput title={translations.model.order.cards.notes.dispatcherNotes} fieldKey="dispatcherNotes" model={model} />
                        <ModelInput title={translations.model.order.cards.notes.employeeNotes} fieldKey="employeeNotes" model={model} />
                    </Card>
                </GridItem>
                <GridItem colSpan={isDesktop ? 5 : 1}>
                    <Card {...translations.model.order.cards.holdInfo}>
                        <ModelInput title={translations.model.order.cards.holdInfo.reason} fieldKey="blocked" model={model} />
                        <ModelInput title={translations.model.order.cards.holdInfo.reason} fieldKey="blockedReason" model={model} />
                    </Card>
                </GridItem>
                <GridItem colSpan={isDesktop ? 5 : 1}>
                    <Card {...translations.model.order.cards.orderRows} rightAddon={<Button onClick={onSearchProduct}>{translations.model.order.cards.orderRows.addRow}</Button>}>
                        <OrderRows model={model} />
                    </Card>
                </GridItem>
                <AttachedFilesGridItem colSpan={isDesktop ? 5 : 1} model={model} previewAttachment={previewAttachmentFromOrder} />
                <GridItem colSpan={isDesktop ? 5 : 1}>
                    <PropertyValuesCard
                        objectType="order"
                        objectId={model.object.id}
                        values={model.object.propertyValues}
                        onChange={(propertyValues) => model.setObject({ ...model.object, propertyValues })}
                    />
                </GridItem>
            </Grid>
        </EditorParent>
    );
};

export default OrderPage;

const onEditRow = (model: Model<Order>, row: OrderRow, translations: Translations, dialog: ReturnType<typeof useDialog>, onAbort?: () => void) => {
    const rowModel = getBasicModelStateless<OrderRow>(OrderRowSchema.parse(row));
    rowModel.setObject({
        ...rowModel.object,
    });
    dialog.openDialog({
        title: translations.model.order.cards.orderRows.editRow,
        body: <EditOrderRow model={rowModel} />,
        buttons: [
            {
                title: translations.common.no,
                onClick: () => {
                    onAbort?.();
                    dialog.closeDialog();
                }
            },
            {
                title: translations.save.save,
                onClick: () => {
                    model.setObject(prevState => {
                        return {
                            ...prevState,
                            orderRows: prevState.orderRows.map(r => r.rowId === row.rowId ? rowModel.object : r)
                        };
                    });

                    dialog.closeDialog();
                }
            }
        ]
    });
}

const OrderRows = ({ model }: { model: Model<Order> }) => {
    const { translations } = React.useContext(Context);
    const dialog = useDialog();
    const onRowClick = (row: OrderRow) => {
        onEditRow(model, row, translations, dialog);
    }

    const tableProps: TableProps<OrderRow> = {
        columns: [
            { title: translations.model.order.cards.orderRows.product, scale: 1, getter: (r) => <ProductName code={r.product.code} icon={r.product.taskIcon} /> },
            { title: translations.model.order.cards.orderRows.orderedQuantity, scale: 1, getter: (r) => r.orderedQuantity },
            { title: translations.model.order.cards.orderRows.actualQuantity, scale: 1, getter: (r) => r.actualQuantity },
        ],
        rows: model.object.orderRows,
        contextMenuActions: [
            {
                title: translations.model.order.cards.orderRows.editRow,
                onClick: onRowClick,
            },
            {
                title: translations.model.order.cards.orderRows.removeRow,
                onClick: (row) => {
                    model.setObject({
                        ...model.object,
                        orderRows: model.object.orderRows.filter(r => r.rowId !== row.rowId)
                    });
                }
            }
        ],
        onRowClick,
    };

    return (
        <Table tableProps={tableProps} />
    );
}

export const ProductSearch = ({ onAddRow }: { onAddRow: (product: Product) => void }) => {
    const { translations } = React.useContext(Context);

    const productHits = useQuery<Product[]>(QueryKeys.products, () => getProducts());
    const tableProps: TableProps<Product> = {
        columns: [
            { title: translations.model.product.id, scale: 1, getter: (r) => r.id },
            { title: translations.model.product.code, scale: 1, getter: (r) => <ProductName code={r.code} icon={r.taskIcon} /> },
        ],
        rows: productHits.data,
        isLoading: productHits.isLoading,
        onRowClick: (row) => {
            onAddRow(row);
        }
    };

    return (
        <Table tableProps={tableProps} />
    );
}

const EditOrderRow = ({ model }: { model: Model<OrderRow> }) => {
    const { translations } = React.useContext(Context);


    return (
        <Box>
            <ModelInput title={translations.model.order.cards.orderRows.orderedQuantity} fieldKey="orderedQuantity" model={model} />
            <ModelInput title={translations.model.order.cards.orderRows.actualQuantity} fieldKey="actualQuantity" model={model} />
        </Box>
    );
}
